import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// === IMPORTY HOOKÓW ===
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useDeviceType } from '../../hooks/useDeviceType'; 

// === IMPORTY ZASOBÓW (NOWE LOGO!) ===
import logo from '../../img/logo_full.png';
import appkaGif from '../../img/apka_gif.gif';
import NotebookBackground from '../../img/NotebookBackground';

// === IMPORTY IKON ===
import { FiShield, FiZap, FiTrendingUp, FiList, FiClock } from 'react-icons/fi';
import { FiMenu, FiX } from 'react-icons/fi';

// === IMPORTY BADGE'Y DO SKLEPÓW ===
import appStoreBadge from '../../img/app-store-badge.svg';
import googlePlayBadge from '../../img/google-play-badge.svg';

// === NOWY IMPORT NASZYCH UTILSÓW ===
import { getFormattedPrices, getGlobalConfig } from '../../utils/configUtils';
import api from '../../api/apiHelper';

const PhoneMockup = ({ src, alt, className = "" }) => (
  <div className={`relative mx-auto border-gray-900 bg-gray-900 border-[8px] md:border-[10px] rounded-[2rem] shadow-xl overflow-hidden max-w-[280px] md:max-w-[300px] ${className}`}>
    {/* Notch (Wcięcie na kamerę) */}
    {/* <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-5 bg-gray-900 rounded-b-xl z-20"></div>
     */}
    {/* Ekran */}
    <div className="h-full w-full bg-white rounded-[1.5rem] overflow-hidden relative z-10">
      <img src={src} className="w-full h-full object-cover" alt={alt} />
    </div>
  </div>
);

const hasPricingConfig = () =>
  Boolean(getGlobalConfig()?.content_variables?.prices_in_grosze?.matura_pp_2027_regular);

const usePricingConfig = () => {
  const [prices, setPrices] = useState(getFormattedPrices);

  useEffect(() => {
    if (hasPricingConfig()) return undefined;

    let cancelled = false;

    api.get('/app-config/', { skipLoading: true })
      .then(({ data }) => {
        localStorage.setItem('global_config', JSON.stringify(data));
        if (!cancelled) {
          setPrices(getFormattedPrices());
        }
      })
      .catch(() => { /* zostajemy przy fallbackach z configUtils */ });

    return () => { cancelled = true; };
  }, []);

  return prices;
};

// --- Karta pakietu ---
const PricingCard = ({ title, subtitle, price, oldPrice, badgeText, highlighted = false }) => (
  <div className={`relative flex flex-col rounded-xl p-6 text-center ${
    highlighted
      ? 'border-2 border-[#E09F2E] bg-[#0a101f]'
      : 'border border-gray-700 bg-[#0a101f]/50'
  }`}>
    {highlighted && badgeText && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#E09F2E] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#0a101f] shadow-lg">
        {badgeText}
      </div>
    )}

    <h4 className={`font-apotek font-semibold uppercase tracking-wider ${highlighted ? 'text-[#E09F2E]' : 'text-[#F0F0F0]'}`}>
      {title}
    </h4>

    <div className="mt-3 flex items-baseline justify-center gap-2">
      {oldPrice && (
        <span className="text-base text-[#F0F0F0]/40 line-through decoration-1">{oldPrice}</span>
      )}
      <span className="font-apotek text-3xl font-bold text-[#F0F0F0]">{price || 'Cena...'}</span>
    </div>

    <p className="mt-1 text-sm text-[#F0F0F0]/60">{subtitle}</p>
  </div>
);

// --- CtaButton ---
const CtaButton = ({ theme = 'light' }) => {
  const deviceType = useDeviceType();

  const greenButtonClasses = "w-full text-center px-8 py-4 text-lg font-semibold text-white bg-[#007D00] rounded-lg shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#007D00]/50 font-apotek uppercase flex items-center justify-center";
  const badgeLinkClasses = "inline-block text-center transition-transform hover:scale-105"; 
  const badgeImageClasses = "w-full max-w-[180px] mx-auto"; 
  
  const isMobile = deviceType === 'iOS' || deviceType === 'Android';
  
  const supportTextClasses = isMobile
    ? "text-sm text-[#E09F2E] mt-6 font-semibold font-apotek uppercase tracking-wider"
    : theme === 'dark'
      ? "text-sm text-[#F0F0F0]/60 mt-2"
      : "text-sm text-[#233855]/80 mt-2";

  const loginLinkClasses = theme === 'dark'
      ? "text-[#F0F0F0] hover:text-white"
      : "text-[#233855] hover:text-black";

  const supportTextContent = isMobile
    ? "3 dni darmowego dostępu bez płatności z góry. Bez haczyków."
    : "Bez płatności z góry. Bez haczyków.";

    const APP_STORE_LINK = "https://apps.apple.com/pl/app/matematly-matura-z-matematyki/id6754283687?l=pl"; // Uzupełnij swoim linkiem
    const GOOGLE_PLAY_LINK = "https://play.google.com/store/apps/details?id=matematly.pl"; // Uzupełnij swoim linkiem

  // 1. iOS -> App Store
  if (deviceType === 'iOS') {
    return (
      <a href={APP_STORE_LINK} className={badgeLinkClasses} target="_blank" rel="noopener noreferrer">
        <img src={appStoreBadge} alt="Pobierz z App Store" className={badgeImageClasses} />
        <p className={supportTextClasses}>{supportTextContent}</p>
      </a>
    );
  }

  // 2. Android -> Google Play
  if (deviceType === 'Android') {
    return (
      <a href={GOOGLE_PLAY_LINK} className={badgeLinkClasses} target="_blank" rel="noopener noreferrer">
        <img src={googlePlayBadge} alt="Pobierz z Google Play" className={badgeImageClasses} />
        <p className={supportTextClasses}>{supportTextContent}</p>
      </a>
    );
  }

  // 3. Desktop / Inne -> Rejestracja Webowa
  return (
    <>
      <Link to="/register" className={greenButtonClasses}>
        Zaczynajmy!
      </Link>
      <p className={supportTextClasses}>{supportTextContent}</p>

      <div className={`mt-2 text-sm ${loginLinkClasses} opacity-90`}>
        Masz już konto?{' '}
        <Link 
          to="/login" 
          className="font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
        >
          Zaloguj się
        </Link>
      </div>
    </>
  );
};

// --- BenefitItem ---
const BenefitItem = ({ icon, title, children }) => (
  <div className="flex items-start gap-4">
    <div className="flex-shrink-0">{icon}</div> 
    <div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-1 opacity-80">{children}</p> 
    </div>
  </div>
);

// --- StepItem ---
const StepItem = ({ number, title, children }) => (
  <div className="flex flex-col items-center text-center">
    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#E09F2E] text-[#0a101f] font-bold text-2xl font-apotek mb-4">
      {number}
    </div>
    <h3 className="text-lg font-semibold font-apotek uppercase mb-2">{title}</h3>
    <p className="opacity-80">{children}</p>
  </div>
);


// --- Dane do FAQ ---
const faqData = [
  {
    question: "Ile to kosztuje?",
    answer: "Aplikacja oferuje 3 dni darmowego dostępu. Po tym czasie wybierasz jeden z pakietów do matury 2027: samą podstawę, samo rozszerzenie albo oba poziomy razem. Płacisz raz, to nie jest subskrypcja, a dostęp działa aż do dnia matury. Aktualne ceny znajdziesz w sekcji Cennik."
  },
  {
    question: "Czy przy rejestracji trzeba podać kartę kredytową?",
    answer: "Nie. Nasze 3 dni darmowego dostępu nie wymaga podawania żadnych danych płatniczych. Po prostu pobierasz, zakładasz konto i testujesz 100% funkcji."
  },
  {
    question: "Gdzie mogę korzystać z aplikacji?",
    answer: "Uczysz się, jak Ci wygodnie. Matematly działa jako natywna apka mobilna, ale pełna funkcjonalność jest także dostępna przez przeglądarkę internetową. Oznacza to, że możesz płynnie przełączać się między nauką na telefonie, tablecie czy komputerze - Twoje postępy są zawsze zsynchronizowane."
},
    { question: "Czy Matematly ma podstawę i rozszerzenie?", answer: "Tak - Matematly obejmuje zarówno maturę podstawową, jak i rozszerzoną. Możesz uczyć się jednego poziomu albo obu, przełączając tor nauki w aplikacji. W obu przypadkach skupiamy się w 100% na maturze: schematy zadań są powtarzalne, co pozwala nam dopasować algorytm ELO i bazę zadań dokładnie do Twojego celu." },
    { question: "Dlaczego system ratingowy jest lepszy?", answer: "Standardowe testy dają Ci tylko procenty. Nasz algorytm ELO precyzyjnie mierzy Twoje rzeczywiste umiejętności w każdym dziale. To pozwala nam dobierać zadania, które są dla Ciebie idealnym wyzwaniem - ani za łatwe, ani za trudne. Dzięki temu nie marnujesz czasu i robisz ciągłe, widoczne postępy." },
    { question: "Czy Orzeł AI naprawdę działa?", answer: "Tak. Orzeł AI jest wyspecjalizowany w naszych zadaniach. Potrafi nie tylko podać rozwiązanie, ale przede wszystkim wyjaśnić dlaczego należy myśleć w dany sposób, krok po kroku. Jest szczególnie pomocny, gdy oficjalne rozwiązanie jest dla Ciebie zbyt skrótowe lub niezrozumiałe." },
    { question: "Czy sama aplikacja wystarczy do zdania matury?", answer: "Podstawą nauki do matury z matematyki jest samodzielne robienie dużej ilości zadań, analizowanie błędów, powtarzanie materiału. Matematly zapewnia wszystkie te funkcje." },
    { question: "Co jeśli utknę na jakimś zadaniu lub dziale?", answer: "To naturalna część nauki. Po pierwsze, przy każdym rozwiązaniu możesz użyć asystenta Orzeł AI, by uzyskać proste wyjaśnienie, a następnie dostaniesz kolejne zadania tego samego typu, żeby utrwalić właściwy sposób rozwiązania. Po drugie, nasz system ELO sam zauważy, że masz trudność i automatycznie dostosuje poziom kolejnych zadań, by dać Ci szansę na odbudowanie pewności siebie. Możesz też ręcznie przećwiczyć ten dział w 'Treningu Dowolnym'." },
    { question: "Czy moje dane i postępy są bezpieczne?", answer: "Tak. Twoje postępy są bezpiecznie zapisywane na serwerze. Oznacza to, że masz do nich dostęp z różnych urządzeń (np. telefon i komputer) i nie stracisz ich, nawet jeśli zgubisz telefon. Stosujemy standardowe praktyki zabezpieczeń, aby chronić Twoje informacje." },
{ 
        question: "Na jakich metodach nauki opiera się Matematly?", 
        answer: "Nasza aplikacja nie jest tylko zbiorem zadań. Zbudowaliśmy ją w oparciu o udowodnione naukowo metody efektywnej nauki. Wykorzystujemy m.in. Spersonalizowaną Naukę (Adaptive Learning) dzięki rankingowi ELO, Powtarzanie w Odstępach (Spaced Repetition) w module Powtórek oraz Przeplatanie (Interleaving). Dzięki temu Twoja nauka jest nie tylko ustrukturyzowana, ale przede wszystkim skuteczna." 
    },
    { 
        question: "Do czego służą serie i zamrożenia? Czy to tylko zabawa?", 
        answer: "To kluczowy element budowania nawyku. Systematyczność jest ważniejsza niż intensywność. Serie (streaks) motywują Cię do codziennej, choćby krótkiej, nauki. Zamrożenia (❄️) dodaliśmy, bo rozumiemy, że każdy potrzebuje przerwy - dzięki nim możesz odpocząć bez stresu i utraty postępów, co wspiera długoterminową motywację." 
    },
{
    question: "Co jeśli zadania wydają mi się za łatwe?",
    answer: "To świetnie, potraktuj to jako rozgrzewkę. Oznacza to, że masz już solidne podstawy. Nasz algorytm zawsze startuje od najprostszych zadań typu maturalnego CKE. System ELO bardzo szybko (po kilku-kilkunastu zadaniach) zauważy Twoje wysokie kompetencje i automatycznie podniesie poziom trudności, aby dopasować wyzwanie idealnie do Ciebie."
},
{
    question: "Co jeśli zadania wydają mi się za trudne?",
    answer: "To najważniejszy sygnał dla Ciebie i... świetna wiadomość. Właśnie odkryłaś/eś swoje luki. Pamiętaj, nasza baza to 100% zadań typu maturlnego. Skoro aplikacja i tak zaczyna od tych najprostszych, to znaczy, że dokładnie nad tym musisz popracować. Zamiast szukać jeszcze prostszych (czyt. niematuralnych) zadań, zacznij działać. Każde za trudne zadanie to okazja. Użyj Orła AI, przeanalizuj rozwiązanie krok po kroku i ucz się schematów."
},
    {
        question: "Czym się różni Tryb Domyślny od Treningu Dowolnego?",
        answer: "Tryb Domyślny (Personalizowane zadania) to Twój główny trener - sam dobiera Ci zadania ze wszystkich działów, bazując na Twoim rankingu ELO, aby zapewnić Ci najszybszy postęp. Trening Dowolny (Ukierunkowana praktyka) daje Ci pełną kontrolę - to Ty wybierasz konkretny dział lub zagadnienie, które chcesz przećwiczyć tu i teraz."
    }
];

// ===================================================================
// === GŁÓWNY KOMPONENT STRONY ===
// ===================================================================

const GeneralLanding = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Gosc widzi cene powitalna — dostaje ja kazdy nowy uzytkownik do konca dnia rejestracji.
  const prices = usePricingConfig();

  const pricingPlans = [
    {
      key: 'pp',
      title: 'Podstawa',
      subtitle: 'Matura podstawowa 2027',
      price: prices.matura_pp_2027_promo,
      oldPrice: prices.matura_pp_2027_regular,
    },
    {
      key: 'pp_pr',
      title: 'Podstawa + Rozszerzenie',
      subtitle: 'Oba poziomy matury 2027',
      price: prices.matura_pp_pr_2027_promo,
      oldPrice: prices.matura_pp_pr_2027_regular,
      highlighted: true,
      badgeText: 'Najlepsza oferta',
    },
    {
      key: 'pr',
      title: 'Rozszerzenie',
      subtitle: 'Matura rozszerzona 2027',
      price: prices.matura_pr_2027_promo,
      oldPrice: prices.matura_pr_2027_regular,
    },
  ];

  const handleFaqToggle = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Funkcja do płynnego scrollowania
  const handleScrollTo = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false); 
  };
  
  return (
    <div className="relative min-h-screen w-full">
      <NotebookBackground />
      
      {/* === NAWIGACJA === */}
      <nav className="fixed top-0 left-0 right-0 bg-blue-950 z-50 shadow-md text-[#F0F0F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            
            {/* 1. Logo i Nazwa */}
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Matematly Logo" className="h-10 w-auto" />
              <span className="text-xl font-bold font-apotek uppercase tracking-wider">
                Matematly
              </span>
            </Link>

            {/* 2. Menu Desktopowe */}
            <div className="hidden md:flex items-center gap-6 text-sm font-medium">
              <a href="#dla-ucznia" onClick={(e) => handleScrollTo(e, 'dla-ucznia')} className="opacity-80 hover:opacity-100 transition-opacity">Dla Ucznia</a>
              <Link to="/dla-szkol" className="opacity-80 hover:opacity-100 transition-opacity">Dla Szkół</Link>
              <a href="#cennik" onClick={(e) => handleScrollTo(e, 'cennik')} className="opacity-80 hover:opacity-100 transition-opacity">Cennik</a>
              <a href="#faq" onClick={(e) => handleScrollTo(e, 'faq')} className="opacity-80 hover:opacity-100 transition-opacity">FAQ</a>
              <Link to="/register" className="ml-4 rounded-md bg-[#007D00] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:scale-105 hover:bg-green-600 font-apotek uppercase">
                Zaczynajmy!
              </Link>
            </div>

            {/* 3. Przycisk Menu Mobilnego */}
            <div className="md:hidden">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2">
                {isMobileMenuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
              </button>
            </div>

          </div>
        </div>

        {/* 4. Rozwijane Menu Mobilne */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-blue-950 shadow-lg z-50 p-6 flex flex-col gap-6 text-lg font-semibold">
            <a href="#dla-ucznia" onClick={(e) => handleScrollTo(e, 'dla-ucznia')}>Dla Ucznia</a>
            <Link to="/dla-szkol" onClick={() => setIsMobileMenuOpen(false)}>Dla Szkół</Link>
            <a href="#cennik" onClick={(e) => handleScrollTo(e, 'cennik')}>Cennik</a>
            <a href="#faq" onClick={(e) => handleScrollTo(e, 'faq')}>FAQ</a>
            
            <hr className="border-gray-700" />
            
            <Link to="/register" className="w-full text-center px-6 py-3 font-semibold text-white bg-[#007D00] rounded-lg shadow-lg font-apotek uppercase">
              Zaczynajmy! 
            </Link>

            <hr className="border-gray-700" />

            <div className="text-sm opacity-70 flex gap-x-4 gap-y-1 justify-center flex-wrap">
              <Link to="/terms-of-service" onClick={() => setIsMobileMenuOpen(false)}>Regulamin</Link>
              <Link to="/privacy-policy" onClick={() => setIsMobileMenuOpen(false)}>Polityka Prywatności</Link>
              {/* === NOWE: WSPARCIE W MOBILE MENU === */}
              <Link to="/support" onClick={() => setIsMobileMenuOpen(false)}>Wsparcie</Link>
            </div>
          </div>
        )}
      </nav>
      {/* === KONIEC NAWIGACJI === */}


      <div className="relative z-10 pt-20"> 
        
        {/* ----- SEKCJA 1: HERO ----- */}
        <header className="bg-blue-950 pt-20 pb-12 px-6 text-center text-[#F0F0F0]">
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-balance font-apotek uppercase">
            Znasz to uczucie? Uczysz się, a potem i tak masz pustkę w głowie?
          </h1>
          <p className="text-xl lg:text-2xl text-[#F0F0F0]/80 max-w-3xl mx-auto mb-10 text-balance">
            Potrzebujesz mądrzejszej nauki. Aplikacja Matematly zapewnia spersonalizowany trening maturalny, zamienia Twoją pracę w realny postęp i pomaga Ci osiągnąć sukces.
          </p>
          <div className="flex flex-col gap-3 max-w-md mx-auto text-center">
            <CtaButton theme="dark" />
          </div>
        </header>

        {/* ----- SEKCJA 2: DLA UCZNIA ----- */}
        <section id="dla-ucznia" className="bg-transparent py-20 px-6 text-[#233855]">
          <h2 className="text-3xl font-bold text-center mb-12 font-apotek uppercase">
            Ucz się efektywnie
          </h2>
          <div className="max-w-2xl mx-auto grid grid-cols-1 gap-8">
            <div className="bg-white rounded-lg shadow-xl p-6">
              <BenefitItem 
                icon={<FiShield size={32} className="text-[#E09F2E]" />} 
                title="Spersonalizowana nauka"
              >
                Nasz algorytm rankingowy ELO dopasowuje zadania do Twojego poziomu, dzięki czemu <b>nie marnujesz czasu</b> i <b>robisz szybkie postępy</b>. Algorytm dba też o powtórki, więc <b>na bieżąco utrwalasz wiedzę</b>.
              </BenefitItem>
            </div>
            <div className="bg-white rounded-lg shadow-xl p-6">
              <BenefitItem 
                icon={<FiZap size={32} className="text-[#E09F2E]" />} 
                title="Tylko matura"
              >
                Rozwiązujesz <b>wyłącznie zadania typu maturalnego</b>. Nie marnujesz czasu na nic innego. Pełna integracja z Kartą Wzorów CKE sprawi, że <b>będziesz wiedzieć, jak ją wykorzystać na maturze</b>.
              </BenefitItem>
            </div>
            <div className="bg-white rounded-lg shadow-xl p-6">
              <BenefitItem 
                icon={<FiTrendingUp size={32} className="text-[#E09F2E]" />} 
                title="Błąd to okazja do nauki"
              >
                Zrobisz coś źle? Od razu dostajesz <b>rozwiązanie krok po kroku</b>, a nasz Orzeł AI wyjaśni je jeszcze lepiej. Algorytm od razu podsunie Ci kolejne zadania tego samego typu, aby <b>zamienić pomyłkę w nową umiejętność.</b>
              </BenefitItem>
            </div>
            <div className="bg-white rounded-lg shadow-xl p-6">
              <BenefitItem 
                icon={<FiList size={32} className="text-[#E09F2E]" />} 
                title="Pełna kontrola"
              >
                Potrzebujesz przećwiczyć tylko logarytmy? W Trybie Treningu Dowolnego możesz wybrać <b>konkretny typ zadania</b>. Daje Ci to pełną kontrolę, aby progresować dokładnie tam, <b>gdzie tego najbardziej potrzebujesz.</b>
              </BenefitItem>
            </div>
          </div>
        </section>

        {/* ----- SEKCJA 3: KROKI + CENNIK ----- */}
        <section id="cennik" className="bg-blue-950 py-20 px-6 text-[#F0F0F0]">
          <h2 className="text-3xl font-bold text-center mb-4 font-apotek uppercase">
            To nie jest kolejny drogi kurs. To uczciwa oferta
          </h2>
          <p className="text-lg text-[#F0F0F0]/80 text-center max-w-2xl mx-auto mb-12 text-balance">
            Oto nasza propozycja dla Ciebie w 3 prostych krokach:
          </p>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
            <StepItem number="1" title="Pobierasz apkę">
              Dostajesz 3 dni darmowego dostępu. Nie prosimy Cię o dodawanie metody płatności.
              Dostajesz 100% funkcji. Bez reklam, bez ściemy.
            </StepItem>
            <StepItem number="2" title="Testujesz 3 dni">
              Sprawdzasz, czy Matematly Ci pasuje. Uczysz się kiedy chcesz i jak chcesz.
            </StepItem>
            <StepItem number="3" title="Decydujesz">
              Jeśli Ci się nie spodoba, po prostu odinstalujesz aplikację. Nic nie tracisz.
            </StepItem>
          </div>

          {/* Cennik */}
          <div className="max-w-4xl mx-auto mt-16 text-center">
            <h3 className="text-2xl font-bold font-apotek uppercase mb-4">A jeśli Ci się spodoba?</h3>
            <p className="text-lg text-[#F0F0F0]/80 mb-6">
              Płacisz raz. <span className="font-bold">To nie jest subskrypcja. </span>
              Dostęp działa od dzisiaj aż do matury w maju 2027.
            </p>

            <div className="mb-8">
              <div className="inline-flex items-center gap-2 rounded-lg bg-[#E09F2E]/15 px-4 py-2">
                <FiClock className="text-[#E09F2E] flex-shrink-0" size={18} />
                <span className="text-sm font-medium text-[#E09F2E]">Cena dla ambitnych — odblokujesz ją pierwszym celem dziennym w aplikacji.</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4">
              {pricingPlans.map((plan) => (
                <PricingCard key={plan.key} {...plan} />
              ))}
            </div>
          </div>
        </section>

        {/* ----- SEKCJA 4: CTA Z GIFEM ----- */}
        <section id="jak-to-dziala" className="bg-transparent py-20 px-6 text-center text-[#233855]">
                    <div className="relative mb-12">
                          {/* Efekt poświaty pod telefonem - to ukrywa niedoskonałości GIFa i robi efekt WOW */}
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full  blur-[60px] rounded-full -z-10"></div>
                          
                          {/* PHONE MOCKUP z GIFem */}
                          <PhoneMockup src={appkaGif} alt="Animacja działania aplikacji" className="shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]" />
                      </div>
          <h2 className="text-3xl font-bold mb-4 text-balance font-apotek uppercase">
            KONIEC Z PUSTKĄ W GŁOWIE
          </h2>
          
          <p className="text-lg text-[#233855]/80 max-w-xl mx-auto mb-8 text-balance">
            Zobacz, jak z zadania na zadanie rośnie Twoja pewność siebie. 
          </p>
          
          <div className="flex flex-col gap-4 max-w-md mx-auto text-center"> 
            <CtaButton />
            {/* <Link to="/login" className="text-gray-600 hover:text-[#233855] mt-4 inline-block">
              Mam już konto
            </Link> */}
          </div>
        </section>
        
        {/* ----- SEKCJA 5: FAQ ----- */}
        <section id="faq" className="bg-transparent py-6 px-6 text-[#233855]">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-xl p-8 lg:p-12">
            <h2 className="text-3xl font-bold mb-4 text-balance font-apotek uppercase">
              Najczęściej zadawane pytania
            </h2>
            <p className="text-lg text-[#233855]/80 max-w-xl mb-8 text-balance">
              Wszystko, co musisz wiedzieć, zanim zaczniesz robić postępy.
            </p>
            
            <dl className="space-y-4">
              {faqData.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <dt>
                    <button onClick={() => handleFaqToggle(index)} className="flex w-full items-center justify-between text-left text-gray-900">
                      <span className="text-base font-semibold">{faq.question}</span>
                      <span className="ml-6 flex h-7 items-center">
                        {openFaqIndex === index ? (
                          <FiX className="h-5 w-5" />
                        ) : (
                          <FiMenu className="h-5 w-5" />
                        )}
                      </span>
                    </button>
                  </dt>
                  <dd className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === index ? 'max-h-96 mt-4' : 'max-h-0'}`}>
                    <p className="text-base text-gray-700">{faq.answer}</p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ----- SEKCJA 6: STOPKA ----- */}
        <footer id="footer" className="bg-blue-950 text-[#F0F0F0]/70">
          <div className="max-w-7xl mx-auto py-16 px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-12">
            
            {/* Kolumna 1: Logo i Copyright */}
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={logo} alt="Matematly Logo" className="h-10 w-auto" />
                <span className="text-xl font-bold font-apotek uppercase tracking-wider text-white">
                  Matematly
                </span>
              </div>
              <p className="text-sm">
                © {new Date().getFullYear()} Matematly. Wszelkie prawa zastrzeżone.
              </p>
            </div>
            
            {/* Kolumna 2: Produkt */}
            <div>
              <h4 className="font-semibold text-white uppercase tracking-wider mb-4">Produkt</h4>
              <nav className="flex flex-col gap-3 text-sm">
                <a href="#dla-ucznia" onClick={(e) => handleScrollTo(e, 'dla-ucznia')} className="hover:text-white">Dla Ucznia</a>
                <Link to="/dla-szkol" className="hover:text-white">Dla Szkół</Link>
                <a href="#cennik" onClick={(e) => handleScrollTo(e, 'cennik')} className="hover:text-white">Cennik</a>
              </nav>
            </div>

            {/* Kolumna 3: Zasoby */}
            <div>
              <h4 className="font-semibold text-white uppercase tracking-wider mb-4">Zasoby</h4>
              <nav className="flex flex-col gap-3 text-sm">
                <Link to="/terms-of-service" className="hover:text-white">Regulamin</Link>
                <Link to="/privacy-policy" className="hover:text-white">Polityka Prywatności</Link>
                {/* === NOWE: WSPARCIE === */}
                <Link to="/support" className="hover:text-white">Wsparcie</Link>
                <a href="#faq" onClick={(e) => handleScrollTo(e, 'faq')} className="hover:text-white">FAQ</a>
              </nav>
            </div>

            {/* Kolumna 4: Kontakt */}
            <div>
              <h4 className="font-semibold text-white uppercase tracking-wider mb-4">Kontakt</h4>
              <div className="flex flex-col gap-3 text-sm">
                <a href="mailto:kontakt@matematly.pl" className="hover:text-white">kontakt@matematly.pl</a>
                <div className="mt-2">
                  <p>Matematly Sp. z o. o. </p>
                  <p>Aleja Powstania Warszawskiego 15</p>
                  <p>31-539 Kraków</p>
                  <p className="opacity-90">NIP: 6751821918</p>
                </div>
              </div>
            </div>

          </div>
        </footer>

      </div>
    </div> 
  );
};

export default GeneralLanding;